import React, { ReactNode, useEffect, useState } from "react"
import { toast } from "react-toastify"

import { useTranslation } from "react-i18next"

import { CardWrapper } from "features/user/cards/CardWrapper"

import { countries } from "constants/countries"

interface BlockedGeosSelectorProps {
  blockedGeos: string[]
  onChange: (geos: string[]) => void
  onSave: () => Promise<boolean>
  /** Extra actions rendered beside Save, e.g. the settings reset for this group. */
  actions?: ReactNode
}

export const BlockedGeosSelector: React.FC<BlockedGeosSelectorProps> = ({ blockedGeos, onChange, onSave, actions }) => {
  const { t } = useTranslation("translation", { useSuspense: false })
  const [geoInput, setGeoInput] = useState("")
  const [geoSuggestions, setGeoSuggestions] = useState<{ name: string; value: string }[]>([])

  useEffect(() => {
    if (!geoInput.trim()) {
      setGeoSuggestions([])
      return
    }
    const filtered = countries
      .filter((c) => c.name.toLowerCase().includes(geoInput.toLowerCase()) && !blockedGeos.includes(c.value))
      .slice(0, 10)
    setGeoSuggestions(filtered)
  }, [geoInput, blockedGeos])

  const addBlockedGeo = (code: string) => {
    onChange([...blockedGeos, code])
    setGeoInput("")
    setGeoSuggestions([])
  }

  const removeBlockedGeo = (code: string) => {
    onChange(blockedGeos.filter((g) => g !== code))
  }

  const handleSave = async () => {
    const success = await onSave()
    if (success) toast.success(t("settings.geo.updated") as string)
  }

  return (
    <CardWrapper>
      <section className="flex flex-col gap-4">
        <h2 className="text-xl font-semibold mb-6 text-[rgba(6,7,21,1)] dark:text-white">{t("settings.geo.title")}</h2>

        <div className="relative">
          <input
            type="text"
            className="w-full px-3 py-2 rounded-lg border bg-white text-black border-black/10 placeholder:text-gray-400 focus:outline-none focus:ring-2 focus:ring-[rgba(169,78,206,0.35)] dark:border-white/10 dark:bg-[#0C0C1A] dark:text-white dark:placeholder:text-gray-500"
            placeholder={t("settings.geo.searchPlaceholder")}
            value={geoInput}
            onChange={(e) => setGeoInput(e.target.value)}
          />

          {geoSuggestions.length > 0 && (
            <ul className="absolute z-10 w-full mt-1 rounded-lg border border-black/10 bg-white shadow max-h-48 overflow-y-auto dark:border-white/10 dark:bg-[#0C0C1A]">
              {geoSuggestions.map((country) => (
                <li
                  key={country.value}
                  className="px-3 py-2 cursor-pointer text-[rgba(6,7,21,1)] hover:bg-black/[0.05] dark:text-white dark:hover:bg-white/[0.06]"
                  onClick={() => addBlockedGeo(country.value)}
                >
                  {country.name}
                </li>
              ))}
            </ul>
          )}
        </div>

        <div className="flex flex-wrap gap-2 mb-2 p-4 rounded-lg border border-black/10 bg-black/[0.02] dark:bg-white/[0.04] dark:border-white/10">
          {blockedGeos.map((code) => (
            <span
              key={code}
              className="flex items-center gap-2 rounded-md text-sm py-2 px-3 border border-black/10 bg-white text-[rgba(6,7,21,1)] hover:bg-black/[0.05] transition-all dark:bg-[#0C0C1A] dark:text-white dark:border-white/10 dark:hover:bg-white/[0.06]"
            >
              {code}
              <button
                type="button"
                onClick={() => removeBlockedGeo(code)}
                className="text-lg leading-3 max-h-[10px] text-gray-500 hover:text-gray-800 dark:text-[#838383] dark:hover:text-white"
                aria-label={`Remove ${code}`}
              >
                ×
              </button>
            </span>
          ))}
        </div>

        <div className="flex flex-wrap items-center justify-end gap-3">
          {actions}
          <button
            className="btn btn-primary bg-transparent text-[rgba(169,78,206,1)] border-[rgba(169,78,206,0.8)] hover:bg-[rgba(169,78,206,0.7)] hover:border-transparent dark:text-white hover:text-white"
            onClick={handleSave}
          >
            {t("common.saveChanges")}
          </button>
        </div>
      </section>
    </CardWrapper>
  )
}
